import {
  IColumn,
  IDetailsGroupDividerProps,
  IDetailsRowProps,
  IRenderFunction,
  IStyle,
  mergeStyles,
} from '@fluentui/react';
import { useControllableValue } from '@fluentui/react-hooks';
import {
  Button,
  buttonStylesPrimary,
  buttonStylesStealth,
  DetailsList,
  DetailsListSizes,
  IconButton,
  IDetailsListProps,
  IH2OTheme,
  Link,
  linkStylesBlack,
  ListRow,
  Loader,
  Search,
  Sizes,
  useClassNames,
  useTheme,
  WidgetPanel,
} from '@h2oai/ui-kit';
import React from 'react';

import { ClassNamesFromIStyles } from '../../utils/models';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';

export type WidgetListProps = {
  title?: string;
  columns: IColumn[];
  items?: unknown[];
  loading: boolean;
  NoItemsContent?: JSX.Element;
  ErrorContent?: JSX.Element;
  delayLoader?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  isLoadingSearch?: boolean;
  searchProps?: {
    onSearchChange: (searchText: string) => void;
    placeholder?: string;
    searchText?: string; // passing undefined will make the search bar controlled, to make it uncontrolled pass an empty string
  };
  actionProps?: {
    actionTitle: string;
    onActionClick: () => void;
    actionIcon?: string;
  };
  customActions?: JSX.Element;
  topPanelSlot?: React.ReactNode;
  forceShowActionBar?: boolean;
  lastRefreshed?: string;
  onRefresh?: () => void;
  groupProps?: {
    getGroupHeight?: (group: any) => number;
    groupProps?: {
      collapseAllVisibility?: number;
      onRenderHeader?: IRenderFunction<IDetailsGroupDividerProps>;
      showEmptyGroups?: boolean;
    };
    groups: any[];
  };
  compact?: boolean;
  styles?: Partial<IWidgetListStyles>;
  onRenderRow?: IRenderFunction<IDetailsRowProps>;
  contentStyles?: IStyle;
};

interface IWidgetListStyles {
  actionWidget: IStyle;
  detailsListContainer: IStyle;
  lastRefreshedWidget: IStyle;
  loadMore: IStyle;
  loadMoreLoader: IStyle;
  loadSearch: IStyle;
  noHeader: IStyle;
  noItemsContainer: IStyle;
  noRunnablesContainer: IStyle;
  refreshButton: IStyle;
  searchBar: IStyle;
  widgetContainer: IStyle;
}

const widgetListStyles = (theme: IH2OTheme): Partial<IWidgetListStyles> => ({
    detailsListContainer: { height: '100%', display: 'flex', flexDirection: 'column', gap: 20 },
    noRunnablesContainer: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      justifyContent: 'center',
      padding: '40px 20px',
    },
    loadMore: {
      display: 'flex',
      justifyContent: 'center',
      padding: '20px 0',
      borderWidth: 1,
      borderTopStyle: 'solid',
      borderColor: theme.semanticColors?.inputBorder,
      borderRadius: 4,
      // TODO: Use theme colors.
      backgroundImage: 'linear-gradient(to top, rgba(255,255,255,0), rgba(255,255,255, 1) 90%)',
      '&:hover': {
        borderColor: theme.semanticColors?.buttonPrimaryBackground,
        cursor: 'pointer',
        button: {
          textDecoration: 'none',
        },
        'button:active': {
          textDecoration: 'none',
        },
      },
      span: {
        color: theme.semanticColors?.textSecondary,
      },
      '&:hover span': {
        color: theme.semanticColors?.textPrimary,
      },
    },
    loadMoreLoader: {
      display: 'flex',
      justifyContent: 'center',
      padding: '10px 0',
    },
    loadSearch: {
      height: '60%',
      alignItems: 'center',
    },
    noItemsContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px 0',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: theme.semanticColors?.inputBorder,
      backgroundImage: theme.semanticColors?.bodyBackground,
      borderRadius: 4,
      height: '60%',
    },
    lastRefreshedWidget: {
      display: 'flex',
      flexGrow: 1,
      alignItems: 'center',
      justifyContent: 'flex-end',
      color: theme.semanticColors?.textSecondary,
      fontSize: 14,
    },
    actionWidget: {
      marginInlineStart: 'auto',
    },
    refreshButton: {
      marginLeft: 10,
    },
    widgetContainer: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      gap: 16,
    },
    noHeader: {
      '& .h2o-WidgetPanel-header': {
        display: 'none',
      },
    },
    searchBar: {
      flex: `0 0 360px`,
    },
  }),
  getWidgetStyles = (compact = false, autoHeight = false) => ({
    controls: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    header: {
      padding: 0,
      paddingBottom: 16,
    },
    root: {
      background: 'transparent',
      borderRadius: 'none',
      padding: compact ? 0 : '20px 10px',
      // Adds extra margin to prevent "Need help?" button from overlapping.
      margin: compact ? 0 : '0px 20px',
      maxWidth: '100%',
      height: compact || autoHeight ? 'auto' : undefined,
    },
    title: {
      fontSize: 16,
      lineHeight: '20px',
    },
  });

const WidgetList = ({
  title,
  columns,
  items,
  loading,
  NoItemsContent,
  ErrorContent,
  onLoadMore,
  isLoadingMore,
  isLoadingSearch,
  searchProps,
  actionProps,
  customActions,
  forceShowActionBar,
  delayLoader = true,
  lastRefreshed,
  onRefresh,
  groupProps,
  compact = false,
  topPanelSlot,
  styles,
  onRenderRow,
  contentStyles,
}: WidgetListProps) => {
  const theme = useTheme(),
    classNames = useClassNames<IWidgetListStyles, ClassNamesFromIStyles<IWidgetListStyles>>(
      'widgetList',
      widgetListStyles,
      styles
    ),
    rootRef = React.useRef<HTMLDivElement>(null),
    hasSomeItem =
      !loading && Boolean(items?.length || (groupProps?.groupProps?.showEmptyGroups && groupProps?.groups.length)),
    timeoutRef = React.useRef<number>(),
    [showLoader, setShowLoader] = React.useState<boolean>(!delayLoader),
    [searchText, setSearchText] = useControllableValue(searchProps?.searchText, ``),
    isSearchStr = Boolean(searchText),
    // TODO: Use useMemo to avoid creating the object on every render.
    listProps: Partial<IDetailsListProps> = {
      styles: {
        root: {
          // Custom styles as "detailsListStylesCard" from ui-kit cannot be extended.
          '.ms-List > .ms-List-surface > .ms-List-page > .ms-List-cell': {
            border: `1px solid ${theme.palette?.gray300}`,
            backgroundColor: theme.palette?.white,
            borderRadius: Sizes.borderRadius,
            marginBottom: DetailsListSizes.GroupPageMarginBottom,
          },
          '.ms-GroupedList-group .ms-List > .ms-List-surface > .ms-List-page > .ms-List-cell': {
            borderLeft: 'none !important',
            borderRight: 'none !important',
            borderBottom: 'none !important',
            borderTop: `1px solid ${theme.palette?.gray200}`,
          },
        },
      },
      focusZoneProps: { style: { width: '100%' } },
      isHeaderVisible: false,
      cellStyleProps: {
        cellExtraRightPadding: 0,
        cellLeftPadding: 0,
        cellRightPadding: 0,
      },
      onRenderDetailsFooter: () => {
        return onLoadMore ? (
          isLoadingMore && showLoader ? (
            <Loader className={classNames.loadMoreLoader} />
          ) : (
            <div className={classNames.loadMore} onClick={onLoadMore}>
              <Link styles={linkStylesBlack}>Load more...</Link>
            </div>
          )
        ) : null;
      },
      onRenderRow: onRenderRow
        ? onRenderRow
        : (props?: { item: { [key: string]: any }; columns?: IColumn[] }) => {
            if (!props) return null;
            const { item, columns = [] } = props;
            return (
              <ListRow
                data={item}
                columns={columns}
                contents={item?.contents}
                hasLeftOpenClose={item?.hasLeftOpenClose}
                hasOpenClose={item?.hasLeftOpenClose}
                openCloseByButton={item?.hasLeftOpenClose}
                collapsed={item?.collapsed}
                styles={{
                  row: {
                    '&:hover': {
                      background: theme.semanticColors?.contentBackground,
                      '.h2o-ListCell-text': {
                        color: theme.semanticColors?.textSecondary,
                      },
                    },
                  },
                  details: contentStyles ?? {
                    paddingLeft: 24,
                    paddingRight: 24,
                  },
                }}
              />
            );
          },
      ...groupProps,
    };

  React.useEffect(() => {
    if (delayLoader) {
      if (loading || isLoadingMore || isLoadingSearch) {
        timeoutRef.current = window.setTimeout(() => setShowLoader(true), 600);
      } else {
        setShowLoader(false);
        window.clearTimeout(timeoutRef.current);
      }
    }
    return () => window.clearTimeout(timeoutRef.current);
  }, [loading, isLoadingMore, isLoadingSearch]);

  const initialUseEffect = React.useRef<boolean>(true);
  React.useEffect(() => {
    if (!initialUseEffect.current) {
      initialUseEffect.current = false;
      return;
    }
    if (isLoadingMore === false) {
      const detailsList = document.querySelector('.h2o-Protected-main');
      if (detailsList) {
        detailsList.scrollTop = rootRef.current?.scrollHeight ?? 0;
      }
    }
  }, [isLoadingMore]);

  return (
    <>
      {hasSomeItem || isSearchStr || forceShowActionBar ? (
        <WidgetPanel
          title={title}
          styles={getWidgetStyles(compact, forceShowActionBar)}
          className={title ? undefined : classNames.noHeader}
        >
          <div className={classNames.detailsListContainer} ref={rootRef}>
            {actionProps || customActions || lastRefreshed || searchProps ? (
              <div className={classNames.widgetContainer}>
                {searchProps && (
                  <Search
                    searchBoxProps={{ value: searchText }}
                    onSearchTextChange={(text) => {
                      setSearchText(text);
                      searchProps.onSearchChange(text);
                    }}
                    placeholder={searchProps?.placeholder || `Search`}
                    className={classNames.searchBar}
                  />
                )}
                {customActions}
                {lastRefreshed ? (
                  <div className={classNames.lastRefreshedWidget}>
                    <>
                      Last refreshed: {formatDefaultDateAndTimeWithSeconds(lastRefreshed) || 'Never'}
                      {onRefresh ? (
                        <IconButton
                          iconName="Refresh"
                          title="Refresh manually"
                          styles={buttonStylesStealth}
                          className={classNames.refreshButton}
                          onClick={onRefresh}
                        />
                      ) : null}
                    </>
                  </div>
                ) : null}
                {actionProps ? (
                  <div className={classNames.actionWidget}>
                    <Button
                      text={actionProps.actionTitle}
                      onClick={actionProps.onActionClick}
                      iconProps={actionProps?.actionIcon ? { iconName: actionProps.actionIcon } : undefined}
                      styles={buttonStylesPrimary}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {topPanelSlot}
            {isLoadingSearch && showLoader ? (
              <div className={mergeStyles(classNames.loadMore, classNames.loadSearch)}>
                <Loader label="Searching..." />
              </div>
            ) : isSearchStr && !hasSomeItem ? (
              <div className={classNames.noItemsContainer}>No items found.</div>
            ) : (
              <DetailsList {...listProps} columns={columns} items={items || []} />
            )}
          </div>
        </WidgetPanel>
      ) : null}
      {!isSearchStr ? (
        <div className={classNames.noRunnablesContainer}>
          {loading ? (
            showLoader ? (
              <Loader label="Loading..." />
            ) : null
          ) : items !== undefined ? (
            !hasSomeItem && (NoItemsContent || <div className={classNames.noItemsContainer}>No items found.</div>)
          ) : (
            ErrorContent || <div>Failed to load items.</div>
          )}
        </div>
      ) : null}
    </>
  );
};

export default WidgetList;
